/**
 * Displays log output, with integrated search.
 */

import {type LogLineData} from '@wandb/weave/common/components/Log/LogLineRenderer';
import {
  edgesToNumberedLines,
  type GlobalLogViewerPagination,
  LogViewer,
  type LogViewerState,
} from '@wandb/weave/common/components/Log/LogViewer';
import debounce from 'lodash/debounce';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import {trackFileDownload} from '../../services/analytics/fileDownloadEvents';
import {
  type LogsPageLocation,
  trackLogsPage,
} from '../../services/analytics/logsPageEvents';
import {useRampFlagRunLogsPagination} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {TabWaveLoader} from '../RunDetails/TabWaveLoader';
import {LogLabelsBanner} from './LogLabelsBanner';
import {LogLimitReachedBanner} from './LogLimitReachedBanner';
import {useRunLogGlobalQuery} from './useRunLogGlobalQuery';
import {useRunLogQuery} from './useRunLogQuery';

type LogViewerScrollData = {
  state: LogViewerState;
  lineCount: number;
  firstLineId: string | undefined;
  lastLineId: string | undefined;
};

interface LogProps {
  analyticsLocation?: LogsPageLocation;
  entityName: string;
  outputLogFile: string | null;
  pollInterval: number;
  projectName: string;
  runName: string;
  scrollPositionCache?: React.MutableRefObject<
    Map<string, LogViewerScrollData>
  >;
  skip?: boolean;
  totalLines?: number;
}

export const Log = ({
  analyticsLocation,
  entityName,
  outputLogFile,
  pollInterval,
  projectName,
  runName,
  scrollPositionCache,
  skip = false,
  totalLines,
}: LogProps) => {
  const isImprovedPaginationEnabled = useRampFlagRunLogsPagination() ?? false;
  const {
    error,
    fetchMoreNext,
    fetchMoreNextError,
    fetchMorePrevious,
    fetchMorePreviousError,
    globalPagination,
    hasNext,
    hasPrevious,
    isLoadingNext,
    isLoadingPrevious,
    isPolling,
    isRetryingNext,
    isRetryingPrevious,
    lines,
    loading,
    retryInitialLoad,
    shouldRestoreScrollState,
  } = useActiveRunLogs({
    entityName,
    isImprovedPaginationEnabled,
    pollInterval,
    projectName,
    runName,
    shouldSkip: skip,
    totalLines,
  });

  const runPath = `${entityName}/${projectName}/${runName}`;
  const {restoreState, handleStateSaved} = useLogViewerScrollState(
    runPath,
    shouldRestoreScrollState ? lines : [],
    scrollPositionCache
  );

  const handleCopyLogs = useCallback(() => {
    if (analyticsLocation) {
      trackLogsPage({
        action: 'Copy logs clicked',
        location: analyticsLocation,
      });
    }
  }, [analyticsLocation]);

  const handleTimestampVisibilityChange = useCallback(
    (isVisible: boolean) => {
      if (analyticsLocation) {
        trackLogsPage({
          action: 'Timestamp visibility toggled',
          location: analyticsLocation,
          isTimestampVisible: isVisible,
        });
      }
    },
    [analyticsLocation]
  );

  const handleLabelFiltersApplied = useCallback(
    (selectedCount: number, isSearchActive: boolean) => {
      if (analyticsLocation) {
        trackLogsPage({
          action: 'Label filters applied',
          location: analyticsLocation,
          numLabelsSelected: selectedCount,
          isSearchActive,
        });
      }
    },
    [analyticsLocation]
  );

  const handleDownload = useCallback(() => {
    if (analyticsLocation) {
      trackFileDownload({
        fileType: 'log',
        isMultipartLogsFile: false,
        location: analyticsLocation,
      });
    }
  }, [analyticsLocation]);

  const handleJumpToBottom = useCallback(() => {
    if (analyticsLocation) {
      trackLogsPage({
        action: 'Jump to bottom clicked',
        location: analyticsLocation,
      });
    }
  }, [analyticsLocation]);

  const handleRetry = useCallback(() => {
    retryInitialLoad?.();
    if (analyticsLocation && retryInitialLoad) {
      trackLogsPage({
        action: 'Retry clicked',
        location: analyticsLocation,
        errorType: 'initial_load',
      });
    }
  }, [analyticsLocation, retryInitialLoad]);

  const handleRetryNext = useCallback(() => {
    fetchMoreNext?.();
    if (analyticsLocation && fetchMoreNext) {
      trackLogsPage({
        action: 'Retry clicked',
        location: analyticsLocation,
        errorType: 'next_page',
      });
    }
  }, [analyticsLocation, fetchMoreNext]);

  const handleRetryPrevious = useCallback(() => {
    fetchMorePrevious?.();
    if (analyticsLocation && fetchMorePrevious) {
      trackLogsPage({
        action: 'Retry clicked',
        location: analyticsLocation,
        errorType: 'previous_page',
      });
    }
  }, [analyticsLocation, fetchMorePrevious]);

  const handleLogsSearched = useMemo(
    () =>
      debounce((searchText: string) => {
        if (analyticsLocation && searchText) {
          trackLogsPage({
            action: 'Logs searched',
            location: analyticsLocation,
            isSearchActive: true,
          });
        }
      }, 3_000),
    [analyticsLocation]
  );

  // Cancel pending the analytics request if the component unmounts.
  useEffect(() => () => handleLogsSearched.cancel(), [handleLogsSearched]);

  const handleSearchResultsNavigated = useCallback(() => {
    if (analyticsLocation) {
      trackLogsPage({
        action: 'Search results navigated',
        location: analyticsLocation,
        isSearchActive: true,
      });
    }
  }, [analyticsLocation]);

  return (
    <LogViewer
      key={runPath}
      error={error}
      globalPagination={globalPagination}
      hasNext={hasNext}
      hasPrevious={hasPrevious}
      isImprovedPaginationEnabled={isImprovedPaginationEnabled}
      isLoadingNext={isLoadingNext}
      isLoadingPrevious={isLoadingPrevious}
      isPolling={isPolling}
      isRetryingNext={isRetryingNext}
      isRetryingPrevious={isRetryingPrevious}
      lines={lines}
      loading={loading}
      loadNextError={fetchMoreNextError}
      loadPreviousError={fetchMorePreviousError}
      onCopyLogs={handleCopyLogs}
      onJumpToBottom={handleJumpToBottom}
      onLabelFiltersApplied={handleLabelFiltersApplied}
      onLoadNext={fetchMoreNext}
      onLoadPrevious={fetchMorePrevious}
      onLogFileDownload={handleDownload}
      onLogSearchResultsNavigated={handleSearchResultsNavigated}
      onLogsSearched={handleLogsSearched}
      onRetry={handleRetry}
      onRetryNext={fetchMoreNext ? handleRetryNext : undefined}
      onRetryPrevious={fetchMorePrevious ? handleRetryPrevious : undefined}
      onStateSaved={shouldRestoreScrollState ? handleStateSaved : undefined}
      onTimestampVisibilityChange={handleTimestampVisibilityChange}
      outputLogFile={outputLogFile}
      renderLabelsBanner={() => (
        <LogLabelsBanner analyticsLocation={analyticsLocation} />
      )}
      renderLimitReachedBanner={numLinesAvailable => (
        <LogLimitReachedBanner numLinesAvailable={numLinesAvailable} />
      )}
      renderLoading={() => <TabWaveLoader />}
      restoreState={shouldRestoreScrollState ? restoreState : undefined}
      totalLines={totalLines}
    />
  );
};

function useActiveRunLogs({
  entityName,
  isImprovedPaginationEnabled,
  pollInterval,
  projectName,
  runName,
  shouldSkip,
  totalLines,
}: {
  entityName: string;
  isImprovedPaginationEnabled: boolean;
  pollInterval: number;
  projectName: string;
  runName: string;
  shouldSkip: boolean;
  totalLines?: number;
}) {
  const useGlobalLogs = isImprovedPaginationEnabled;
  const globalLogs = useRunLogGlobalQuery({
    entityName,
    pollInterval,
    projectName,
    runName,
    skip: shouldSkip || !useGlobalLogs,
  });

  const legacyLogs = useRunLogQuery({
    entityName,
    pollInterval,
    projectName,
    runName,
    skip: shouldSkip || useGlobalLogs,
    useImprovedPagination: false,
  });

  const legacyLines = useMemo(() => {
    if (legacyLogs.loading || !legacyLogs.logLines) {
      return [];
    }

    // keep only edges whose node has the fields LogLineData requires non-null;
    // the generated GraphQL node types them as nullable.
    const eligibleEdges = legacyLogs.logLines.edges.filter(
      (edge): edge is {node: LogLineData} =>
        edge.node.id != null &&
        edge.node.line != null &&
        edge.node.timestamp != null
    );

    return edgesToNumberedLines(eligibleEdges, totalLines);
  }, [legacyLogs.loading, legacyLogs.logLines, totalLines]);

  const globalPagination = useMemo<
    GlobalLogViewerPagination | undefined
  >(() => {
    if (!useGlobalLogs || globalLogs.totalCount == null) {
      return undefined;
    }

    return {
      initialTopMostItemIndex: globalLogs.initialTopMostItemIndex,
      isLoadingRange: globalLogs.isLoadingRange,
      isRetryingRange: globalLogs.isRetryingRangeFetch,
      lineByNumber: globalLogs.globalLinesByNumber,
      newLineCount: globalLogs.newLineCountWhileDetached,
      onAtCurrentChange: globalLogs.setIsAtCurrent,
      onJumpToCurrent: globalLogs.jumpToCurrent,
      onJumpToTop: globalLogs.jumpToTop,
      onRangeChanged: globalLogs.fetchRangeForVisibleRange,
      onRetryRange: globalLogs.retryRangeFetch,
      rangeFetchError: globalLogs.rangeFetchError,
      totalCount: globalLogs.totalCount,
    };
  }, [
    globalLogs.fetchRangeForVisibleRange,
    globalLogs.globalLinesByNumber,
    globalLogs.initialTopMostItemIndex,
    globalLogs.isLoadingRange,
    globalLogs.isRetryingRangeFetch,
    globalLogs.jumpToCurrent,
    globalLogs.jumpToTop,
    globalLogs.newLineCountWhileDetached,
    globalLogs.rangeFetchError,
    globalLogs.retryRangeFetch,
    globalLogs.setIsAtCurrent,
    globalLogs.totalCount,
    useGlobalLogs,
  ]);

  if (useGlobalLogs) {
    return {
      error: globalLogs.error,
      fetchMoreNext: undefined,
      fetchMoreNextError: undefined,
      fetchMorePrevious: undefined,
      fetchMorePreviousError: undefined,
      globalPagination,
      hasNext: false,
      hasPrevious: false,
      isLoadingNext: false,
      isLoadingPrevious: false,
      isPolling: globalLogs.isPolling,
      isRetryingNext: false,
      isRetryingPrevious: false,
      lines: globalLogs.lines,
      loading: globalLogs.loading,
      retryInitialLoad: globalLogs.retryInitialLoad,
      shouldRestoreScrollState: false,
    };
  }

  return {
    error: legacyLogs.error,
    fetchMoreNext: legacyLogs.fetchMoreNext,
    fetchMoreNextError: legacyLogs.fetchMoreNextError,
    fetchMorePrevious: legacyLogs.fetchMorePrevious,
    fetchMorePreviousError: legacyLogs.fetchMorePreviousError,
    globalPagination: undefined,
    hasNext: legacyLogs.hasNext,
    hasPrevious: legacyLogs.hasPrevious,
    isLoadingNext: legacyLogs.isLoadingNext,
    isLoadingPrevious: legacyLogs.isLoadingPrevious,
    isPolling: legacyLogs.isPolling,
    isRetryingNext: legacyLogs.isRetryingNext,
    isRetryingPrevious: legacyLogs.isRetryingPrevious,
    lines: legacyLines,
    loading: legacyLogs.loading,
    retryInitialLoad: legacyLogs.retryInitialLoad,
    shouldRestoreScrollState: true,
  };
}

// useLogScrollPositionCache configures a scroll position cache for `Log`. This
// is required when scroll position restoration is needed and the run key for
// `Log` can change without re-mounting the component
export function useLogScrollPositionCache() {
  return useRef(new Map<string, LogViewerScrollData>());
}

function useLogViewerScrollState(
  runPath: string,
  lines: readonly {id: string}[],
  scrollPositionCache?: React.MutableRefObject<Map<string, LogViewerScrollData>>
) {
  // Determine restoreState once — the first time lines are available. After
  // that, Virtuoso owns scroll state internally.
  const restoreStateRef = useRef<LogViewerState | null | undefined>(undefined);
  if (
    // oxlint-disable-next-line react/refs
    scrollPositionCache &&
    // oxlint-disable-next-line react/refs
    restoreStateRef.current === undefined &&
    lines.length > 0
  ) {
    const data = scrollPositionCache.current.get(runPath);
    const isMatchingState =
      data != null &&
      data.lineCount === lines.length &&
      data.firstLineId === lines[0]?.id &&
      data.lastLineId === lines[lines.length - 1]?.id;
    restoreStateRef.current = isMatchingState ? data.state : null;
  }
  // oxlint-disable-next-line react/refs
  const restoreState = restoreStateRef.current ?? undefined;

  const handleStateSaved = useMemo(() => {
    if (!scrollPositionCache) {
      return undefined;
    }

    return (state: LogViewerState) => {
      scrollPositionCache.current.set(runPath, {
        state,
        lineCount: lines.length,
        firstLineId: lines[0]?.id,
        lastLineId: lines[lines.length - 1]?.id,
      });
    };
  }, [scrollPositionCache, runPath, lines]);

  // oxlint-disable-next-line react/refs
  return {restoreState, handleStateSaved};
}
